<template>
  <div class="mini-iq-overlay" data-testid="mini-iq-overlay">
    <div class="mini-iq-overlay__panel">
      <div v-if="hasInFlightResponse" class="mini-iq-overlay__ai-bubble">
        <IQAIResponse
          :response="chatStore.currentResponse"
          :isThinking="chatStore.isThinking"
          :currentReasoning="chatStore.currentReasoning"
          surface="light"
        />
      </div>
      <div v-else-if="terminalTextError" class="mini-iq-overlay__terminal-err" role="alert">
        <p class="mini-iq-overlay__error-text">
          {{ terminalTextError }}
        </p>
        <button type="button" class="mini-iq-overlay__back-btn" @click="$emit('dismiss')">
          Back to video
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import IQAIResponse from "@components/IQAIResponse/IQAIResponse.vue";
import { useIQChatStore } from "@components/IQOverlay/store/iqChatStore";
import configService from "../../account/src/services/config";
import {
  buildVideoToComponentsPayload,
  buildIqVideoSuggestEndpoint,
} from "@/utils/buildVideoToComponentsPayload";

const props = defineProps({
  /** Still sent as the streaming question; not shown in the UI. */
  userPrompt: { type: String, default: "" },
  videoUrl: { type: String, default: "" },
  checkpointPrefix: { type: String, default: "" },
  checkpointCriteria: { type: String, default: "" },
  responsibilityName: { type: String, default: "" },
  responsibilityPurpose: { type: String, default: "" },
  /** Same shape as GenerateVideoMicrolearningDialog emit; null = full inspection-style body */
  videoToComponentsSelection: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["open-review", "dismiss"]);

const chatStore = useIQChatStore();
const { messages } = storeToRefs(chatStore);
const done = ref(false);

const hasInFlightResponse = computed(
  () =>
    chatStore.isThinking ||
    chatStore.isLoading ||
    chatStore.isGenerating ||
    !!chatStore.currentResponse
);

const terminalTextError = computed(() => {
  if (hasInFlightResponse.value) {
    return "";
  }
  const list = (messages.value || []).filter((m) => m.type === "ai");
  const last = list[list.length - 1];
  if (last && typeof last.content === "string" && !chatStore.isThinking) {
    return last.content;
  }
  if (last && last.content && typeof last.content === "object" && !chatStore.isThinking) {
    return last.content.error || last.content.answer || "";
  }
  return "";
});

function extractVideoUrl(iframeHtml) {
  if (!iframeHtml) return "";
  if (!iframeHtml.includes("<iframe") && !iframeHtml.includes("src=")) {
    return iframeHtml;
  }
  const srcMatch = iframeHtml.match(/src=\\?"([^"\\]+)/);
  if (srcMatch && srcMatch[1]) {
    return srcMatch[1];
  }
  const fallbackMatch = iframeHtml.match(/src="([^"]+)"/);
  if (fallbackMatch && fallbackMatch[1]) {
    return fallbackMatch[1];
  }
  return "";
}

function findLastVideoInstructionSuggestions() {
  const list = (chatStore.messages || []).filter((m) => m.type === "ai");
  for (let i = list.length - 1; i >= 0; i--) {
    const c = list[i].content;
    if (c && typeof c === "object" && c.suggestions != null) {
      const s = c.suggestions;
      if (typeof s === "object" && s !== null) {
        return s;
      }
    }
  }
  return null;
}

function inferMismatchSuggestionsFromLastMessage() {
  const list = (chatStore.messages || []).filter((m) => m.type === "ai");
  const last = list[list.length - 1];
  if (!last) return null;

  const videoUrl = extractVideoUrl(props.videoUrl) || props.videoUrl || null;
  const mismatchFallback = "This video does not match the context of the checkpoint!";

  if (last.content && typeof last.content === "object") {
    const explicitError =
      last.content.error ||
      last.content.answer ||
      (typeof last.content.validation?.reasoning === "string"
        ? last.content.validation.reasoning
        : null);
    if (explicitError) {
      return {
        steps: [],
        summary: null,
        error: explicitError || mismatchFallback,
        video_url: videoUrl,
      };
    }
  }

  if (typeof last.content === "string" && last.content.trim()) {
    return {
      steps: [],
      summary: null,
      error: last.content.trim(),
      video_url: videoUrl,
    };
  }

  return null;
}

function tryEmitOpenReviewFromLatestMessage() {
  if (done.value) return;
  const fromMessages = findLastVideoInstructionSuggestions();
  if (fromMessages) {
    done.value = true;
    emit("open-review", { suggestedInstructions: fromMessages });
    return;
  }

  const mismatchSuggestions = inferMismatchSuggestionsFromLastMessage();
  if (mismatchSuggestions) {
    done.value = true;
    emit("open-review", { suggestedInstructions: mismatchSuggestions });
  }
}

async function runFromVideoRequest() {
  const iqBaseUrl = configService.get("iqBaseUrl");
  const apiEndpoint = buildIqVideoSuggestEndpoint(iqBaseUrl, true);
  const currentAccountDetails = JSON.parse(localStorage.getItem("currentAccountDetails") || "{}");
  const businessPurpose = currentAccountDetails.purpose || "";
  const extractedVideoUrl = extractVideoUrl(props.videoUrl);
  const checkpointCriteria = `${props.checkpointPrefix} ${props.checkpointCriteria}`.trim();
  const customPayload = buildVideoToComponentsPayload({
    videoUrl: extractedVideoUrl,
    businessPurpose,
    responsibilityName: props.responsibilityName || "",
    responsibilityPurpose: props.responsibilityPurpose || "",
    checkpointCriteria,
    language: "english",
    microlearningSelection: props.videoToComponentsSelection,
  });
  chatStore.clearChat();
  done.value = false;
  await chatStore.generateAIResponse(props.userPrompt, 5, apiEndpoint, customPayload);
}

let stopGenWatch;
let stopMessagesWatch;
onMounted(() => {
  void runFromVideoRequest();
  stopGenWatch = watch(
    () => chatStore.isGenerating,
    (on, wasOn) => {
      if (done.value || !wasOn || on) {
        return;
      }
      tryEmitOpenReviewFromLatestMessage();
    }
  );
  stopMessagesWatch = watch(
    () => chatStore.messages.length,
    () => {
      tryEmitOpenReviewFromLatestMessage();
    }
  );
});

onUnmounted(() => {
  if (stopGenWatch) {
    stopGenWatch();
  }
  if (stopMessagesWatch) {
    stopMessagesWatch();
  }
});
</script>

<style scoped>
.mini-iq-overlay {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 320px;
  background: #ffffff;
  border-radius: 8px;
  box-sizing: border-box;
  overflow: hidden;
}

.mini-iq-overlay__panel {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px 0px 0px 0px;
  flex: 1;
  min-height: 0;
  overflow: auto;
}

.mini-iq-overlay__ai-bubble {
  display: flex;
  justify-content: flex-start;
  width: 100%;
}

.mini-iq-overlay :deep(.iq-ai-response) {
  width: 100%;
  max-width: 100%;
}

.mini-iq-overlay__terminal-err {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
}

.mini-iq-overlay__error-text {
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 22px;
  color: #b42318;
  margin: 0;
  padding: 12px 14px;
  background: #fef3f2;
  border: 1px solid #fda29b;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;
}

.mini-iq-overlay__back-btn {
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #7f56d9;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
}

.mini-iq-overlay__back-btn:hover {
  text-decoration: underline;
}
::v-deep .iq-ai-response {
  border-radius: 8px;
}
</style>
